/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState } from 'react';

// ── Feature Flags Context ─────────────────────────────────────────────────────
// Stores optional/experimental feature toggles.
// Currently only superadmin users can toggle experimental features in Settings.

const FeatureFlagsContext = createContext(null);

// Storage keys
const EXPERIMENTAL_GRANT_KEY = 'wm-grants-experimental';
const XP_KEY         = 'wm-feature-xp';
const WORKFLOW_KEY   = 'wm-feature-workflow';
const INVOICES_KEY   = 'wm-feature-invoices';
const REPORTS_KEY    = 'wm-feature-reports';
const PORTAL_KEY     = 'wm-feature-client-portal';
const TOOLTIPS_KEY   = 'wm-feature-tooltips';
const MARKETING_KEY  = 'wm-feature-marketing';
const SIMPLE_KEY     = 'wm-simple-mode';
const PLAN_TIER_KEY  = 'wm-plan-tier';

// Plan tiers — controls which paid features are accessible
// 'starter' | 'professional' | 'enterprise'
const VALID_TIERS = ['starter', 'professional', 'enterprise'];
function readTier() {
  try {
    const stored = localStorage.getItem(PLAN_TIER_KEY);
    return VALID_TIERS.includes(stored) ? stored : 'professional';
  } catch {
    return 'professional';
  }
}

function readFlag(key, defaultVal = false) {
  try {
    const stored = localStorage.getItem(key);
    return stored === null ? defaultVal : stored === 'true';
  } catch {
    return defaultVal;
  }
}

export function FeatureFlagsProvider({ children }) {
  const [xpEnabled,           setXpEnabledState]           = useState(() => readFlag(XP_KEY, true));
  const [workflowEnabled,     setWorkflowEnabledState]     = useState(() => readFlag(WORKFLOW_KEY, false));
  const [invoicesEnabled,     setInvoicesEnabledState]     = useState(() => readFlag(INVOICES_KEY, false));
  const [reportsEnabled,      setReportsEnabledState]      = useState(() => readFlag(REPORTS_KEY, false));
  const [clientPortalEnabled, setClientPortalEnabledState] = useState(() => readFlag(PORTAL_KEY, false));
  const [tooltipsEnabled,     setTooltipsEnabledState]     = useState(() => readFlag(TOOLTIPS_KEY, true));
  const [marketingEnabled,    setMarketingEnabledState]    = useState(() => readFlag(MARKETING_KEY, false));
  // Simple Mode — hides advanced/technical nav items for non-technical users
  const [simpleMode,          setSimpleModeState]          = useState(() => readFlag(SIMPLE_KEY, false));
  // Plan tier — drives paid-feature gating
  const [planTier,            setPlanTierState]            = useState(readTier);
  // Experimental grant — explicitly unlocked by WrapMind Senior staff for this account
  const [experimentalGranted, setExperimentalGrantedState] = useState(() => readFlag(EXPERIMENTAL_GRANT_KEY, false));

  const makeFlag = (setState, key) => (v) => {
    setState(v);
    try { localStorage.setItem(key, String(v)); } catch { /* ignore storage failures */ }
  };

  const setXpEnabled           = makeFlag(setXpEnabledState,           XP_KEY);
  const setWorkflowEnabled     = makeFlag(setWorkflowEnabledState,     WORKFLOW_KEY);
  const setInvoicesEnabled     = makeFlag(setInvoicesEnabledState,     INVOICES_KEY);
  const setReportsEnabled      = makeFlag(setReportsEnabledState,      REPORTS_KEY);
  const setClientPortalEnabled = makeFlag(setClientPortalEnabledState, PORTAL_KEY);
  const setTooltipsEnabled     = makeFlag(setTooltipsEnabledState,     TOOLTIPS_KEY);
  const setMarketingEnabled    = makeFlag(setMarketingEnabledState,    MARKETING_KEY);
  const setSimpleMode          = makeFlag(setSimpleModeState,          SIMPLE_KEY);
  const setExperimentalGranted = makeFlag(setExperimentalGrantedState, EXPERIMENTAL_GRANT_KEY);

  const setPlanTier = (tier) => {
    if (!VALID_TIERS.includes(tier)) return;
    setPlanTierState(tier);
    try { localStorage.setItem(PLAN_TIER_KEY, tier); } catch { /* ignore storage failures */ }
  };

  // Derived tier capabilities
  const multiLocationEnabled = planTier === 'enterprise';

  return (
    <FeatureFlagsContext.Provider value={{
      xpEnabled, setXpEnabled,
      workflowEnabled, setWorkflowEnabled,
      invoicesEnabled, setInvoicesEnabled,
      reportsEnabled, setReportsEnabled,
      clientPortalEnabled, setClientPortalEnabled,
      tooltipsEnabled, setTooltipsEnabled,
      marketingEnabled, setMarketingEnabled,
      simpleMode, setSimpleMode,
      planTier, setPlanTier,
      multiLocationEnabled,
      experimentalGranted, setExperimentalGranted,
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  return ctx;
}
